import { useCallback, useRef } from "react";

import { registerSW } from "virtual:pwa-register";

import { ENABLE_SERVICE_WORKER } from "@/constants";
import { ServiceWorkerMessage, ServiceWorkerResponse } from "./types";

const SERVICE_WORKER_CHECK_INTERVAL = 60 * 1000;
const SERVICE_WORKER_ACTIVATION_DELAY = 30 * 1000;

const useServiceWorker = () => {
  const updateIntervalRef = useRef<NodeJS.Timeout>(null);

  const isServiceWorkerAvailable = useCallback(async () => {
    return !!navigator.serviceWorker;
  }, []);

  const checkForNewServiceWorker = useCallback(
    (registration: ServiceWorkerRegistration) => {
      registration.update().then(() => {
        if (registration.waiting) {
          console.debug("[SW] Update found");
          // Delay the activation of the new service worker to try to
          // mitigate race conditions with cloudflare
          setTimeout(() => {
            registration.waiting.postMessage({
              type: ServiceWorkerMessage.SKIP_WAITING,
            });
          }, SERVICE_WORKER_ACTIVATION_DELAY);
        }
      });
    },
    [],
  );

  const registerServiceWorker = useCallback(
    ({
      onFinish,
    }: {
      onFinish: ({ success }: { success: boolean }) => void;
    }) => {
      updateIntervalRef.current && clearInterval(updateIntervalRef.current);
      if (!("serviceWorker" in navigator) || !ENABLE_SERVICE_WORKER) {
        console.warn("This browser does not support service workers");
        onFinish({ success: false });
        return;
      }

      registerSW({
        onRegisterError(e) {
          console.error(
            `There was an error registering the service worker${e}`,
          );
          onFinish({ success: false });
        },
        onRegisteredSW: (
          _swUrl: string,
          registration: ServiceWorkerRegistration,
        ) => {
          try {
            // Check if there's already an active service worker
            if (registration.active) {
              onFinish({ success: true });
            } else {
              registration.addEventListener("updatefound", () => {
                const installingWorker = registration.installing;
                if (installingWorker) {
                  installingWorker.addEventListener("statechange", () => {
                    // Wait until the service worker is active
                    if (installingWorker.state === "activated") {
                      onFinish({ success: true });
                    } else if (installingWorker.state === "redundant") {
                      onFinish({ success: false });
                    }
                  });
                }
              });
            }

            // Check for updates periodically
            updateIntervalRef.current = setInterval(() => {
              checkForNewServiceWorker(registration);
            }, SERVICE_WORKER_CHECK_INTERVAL);
          } catch (e) {
            console.error(
              `There was an error registering the service worker${e}`,
            );
            onFinish({ success: false });
          }
        },
      });
    },
    [checkForNewServiceWorker],
  );

  const sendMessageToSW = useCallback(
    <T>({
      type,
      payload,
    }: {
      type: ServiceWorkerMessage[number];
      payload?: T;
    }) => {
      if (!("serviceWorker" in navigator)) return;

      navigator.serviceWorker.ready.then((registration) => {
        registration.active.postMessage({
          payload,
          type,
        });
      });
    },
    [],
  );

  const startPrecache = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      sendMessageToSW({ type: ServiceWorkerMessage.PRECACHE });
      const messageListener = (
        event: MessageEvent<{
          type: ServiceWorkerResponse[number];
          payload: {
            success: boolean;
          };
        }>,
      ) => {
        if (event.data.type !== ServiceWorkerResponse.PRECACHE_FINISH) return;
        if (event?.data?.payload?.success) {
          resolve();
        } else {
          reject();
        }
        navigator.serviceWorker.removeEventListener("message", messageListener);
      };
      navigator.serviceWorker.addEventListener("message", messageListener);
    });
  }, [sendMessageToSW]);

  const refreshAPICache = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (!isServiceWorkerAvailable()) {
        resolve();
      }
      navigator.serviceWorker.ready
        .then(() => {
          const messageListener = (
            event: MessageEvent<{
              type: ServiceWorkerResponse[number];
              payload: {
                success: boolean;
              };
            }>,
          ) => {
            if (
              event?.data?.type !== ServiceWorkerResponse.READY_FOR_API_REFRESH
            )
              return;
            navigator.serviceWorker.removeEventListener(
              "message",
              messageListener,
            );
            if (event?.data?.payload?.success) {
              resolve();
            } else {
              reject("Could not clear service worker cache");
            }
          };
          navigator.serviceWorker.addEventListener("message", messageListener);
          sendMessageToSW({
            type: ServiceWorkerMessage.PREPARE_FOR_API_REFRESH,
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }, [sendMessageToSW, isServiceWorkerAvailable]);

  const finishAPIRefresh = useCallback(() => {
    sendMessageToSW({ type: ServiceWorkerMessage.API_REFRESH_FINISHED });
  }, [sendMessageToSW]);

  const setForceNetworkFirst = useCallback((value: boolean) => {
    sendMessageToSW({
      payload: { forceNetworkForCartography: value },
      type: ServiceWorkerMessage.UPDATE_STATE,
    });
  }, []);

  return {
    finishAPIRefresh,
    refreshAPICache,
    registerServiceWorker,
    setForceNetworkFirst,
    startPrecache,
  };
};

export default useServiceWorker;
